import { Button, TooltipButton } from '@/components/Button';
import { cn } from '@/lib/cn';
import { faChevronDown, faClock, faThumbTack } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Popover from '@radix-ui/react-popover';
import Tooltip from 'components/tooltip/Tooltip';
import TooltipContainerContext from 'contexts/TooltipContainerContext';
import { ButtonHTMLAttributes, MouseEventHandler, useRef } from 'react';

const Root = ({ children, modal = true, ...props }: Popover.PopoverProps) => {
    return (
        <Popover.Root modal={modal} {...props}>
            {children}
        </Popover.Root>
    );
};

const Trigger = ({ children, disabled, title, className, ...props }: Popover.PopoverTriggerProps) => {
    return (
        <Popover.Trigger
            asChild
            disabled={disabled}
            className={cn('data-[state=open]:bg-secondaryButtonBackgroundHover group')}
            {...props}
        >
            <TooltipButton
                variant='secondary'
                icon={<FontAwesomeIcon icon={faClock} />}
                disabled={disabled}
                title={title}
            >
                {children}
                <FontAwesomeIcon
                    className={cn(
                        'ml-3 text-secondary group-hover:text-textPrimary self-center group-data-[state=open]:rotate-180',
                        disabled && 'text-textDisabled'
                    )}
                    icon={faChevronDown}
                />
            </TooltipButton>
        </Popover.Trigger>
    );
};

interface FilterButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    children: React.ReactNode;
    isActive?: boolean;
    disabled?: boolean;
}

const FilterButton = ({ children, isActive, ...props }: FilterButtonProps) => {
    return (
        <Button
            className={cn(
                'group flex text-textPrimary justify-between w-full leading-input px-xs py-autocomplete hover:bg-dividerPrimary hover:text-textPrimary focus-visible:text-textPrimary focus-visible:bg-dividerTertiary',
                isActive && 'bg-dividerPrimary font-semibold'
            )}
            autoFocus={isActive}
            variant='tertiary'
            {...props}
        >
            {children}
        </Button>
    );
};

const Separator = ({ className }: { className?: string }) => {
    return <hr className={cn('border-dividerPrimary m-xxxs', className)} />;
};

const Group = ({ children, className }: { children: React.ReactNode; className?: string }) => {
    return (
        <div className={cn(className, 'group/group')}>
            {children}
            <Separator className='my-xxxs group-last/group:hidden' />
        </div>
    );
};

const MenuContent = ({
    children,
    className,
    container,
    sideOffset = 5,
    align = 'end',
    ...props
}: Popover.PopoverContentProps & {
    container?: HTMLElement | null;
    zIndex?: number;
}) => {
    const contentRef = useRef<HTMLDivElement>(null);

    return (
        <Popover.Portal container={container}>
            <Popover.Content
                sideOffset={sideOffset}
                className={cn(
                    'z-10 max-h-[--radix-popover-content-available-height] max-w-[--radix-popover-content-available-width] overflow-auto sm:overflow-visible sm:max-h-auto text-textPrimary scrollbar-thin scrollbar-track-transparent scrollbar-thumb-statusUnknownPrimary',
                    className
                )}
                align={align}
                ref={contentRef}
                {...props}
            >
                <TooltipContainerContext.Provider value={contentRef.current}>
                    <div className='flex flex-col sm:flex-row'>{children}</div>
                </TooltipContainerContext.Provider>
            </Popover.Content>
        </Popover.Portal>
    );
};

const FiltersMenu = ({ children, className }: { children: React.ReactNode; className?: string }) => {
    return (
        <div
            className={cn(
                '-order-1 sm:order-1 w-[320px] shrink-0 sm:max-h-[--radix-popover-content-available-height] overflow-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-statusUnknownPrimary p-1 bg-componentBackgroundPrimary border border-dividerPrimary mb-auto',
                className
            )}
        >
            {children}
        </div>
    );
};

const AdvancedMenu = ({ children, className }: { children: React.ReactNode; className?: string }) => {
    return (
        <div
            className={cn(
                'bg-componentBackgroundPrimary sm:shrink border border-dividerPrimary w-[320px] sm:w-[618px] sm:border-r sm:border-r-dividerPrimary sm:max-h-[--radix-popover-content-available-height] overflow-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-statusUnknownPrimary sm:mr-[-1px]',
                className
            )}
        >
            <div className='flex flex-col h-full pt-3'>{children}</div>
        </div>
    );
};

const TimeframePreview = ({ children, className, ...props }: { children: React.ReactNode; className?: string }) => {
    return (
        <span
            className={cn(
                'block font-normal whitespace-nowrap text-textSecondary group-disabled:text-textDisabled',
                className
            )}
            {...props}
        >
            {children}
        </span>
    );
};

const TimeframeLabel = ({ children, className, ...props }: { children: React.ReactNode; className?: string }) => {
    return (
        <span className={cn('block mr-2 whitespace-nowrap', className)} {...props}>
            {children}
        </span>
    );
};

const TimeframePin = ({
    className,
    disabled,
    isActive,
    onClick,
    title,
    ...props
}: {
    className?: string;
    disabled?: boolean;
    isActive?: boolean;
    onClick?: MouseEventHandler<SVGSVGElement>;
    title?: string;
}) => {
    const tooltipTitle = isActive ? 'Default timeframe' : 'Set default timeframe';

    return (
        <span
            className={cn(
                'mr-auto',
                {
                    'opacity-0 group-hover:opacity-100': !isActive
                },
                className
            )}
            {...props}
        >
            <Tooltip title={title || tooltipTitle} placement='right' disabled={disabled}>
                <FontAwesomeIcon
                    className={cn('text-textIncomplete hover:text-textSecondary', {
                        hidden: disabled && !isActive
                    })}
                    fixedWidth
                    icon={faThumbTack}
                    onClick={!isActive && !disabled ? onClick : undefined}
                />
            </Tooltip>
        </span>
    );
};

export default Object.assign(Root, {
    Trigger,
    MenuContent,
    Separator,
    Group,
    FilterButton,
    FiltersMenu,
    AdvancedMenu,
    TimeframePreview,
    TimeframeLabel,
    TimeframePin
});
